/**
 * Contexte hors-ligne : suit l'état réseau, la file de mouvements en attente et la dernière synchro.
 * Rafraîchit l'instantané au montage et au retour du réseau, resynchronise la file automatiquement
 * (au retour du réseau puis toutes les 60 s). Alimente l'indicateur permanent de la barre latérale.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { refreshSnapshot, flushQueue, pendingCount, isNetworkError } from '../offline/store';

const OfflineContext = createContext(null);
const SYNC_INTERVAL_MS = 60_000;

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const busy = useRef(false);

  const refreshPending = useCallback(async () => {
    try { setPending(await pendingCount()); } catch { /* IndexedDB indisponible */ }
  }, []);

  const syncNow = useCallback(async () => {
    if (busy.current || !navigator.onLine) return;
    busy.current = true;
    setSyncing(true);
    try {
      await flushQueue();
      await refreshSnapshot();
      setLastSync(Date.now());
    } catch (e) {
      if (!isNetworkError(e)) setLastSync(Date.now());
    } finally {
      await refreshPending();
      setSyncing(false);
      busy.current = false;
    }
  }, [refreshPending]);

  // État réseau + file d'attente.
  useEffect(() => {
    const onOnline = () => { setOnline(true); syncNow(); };
    const onOffline = () => setOnline(false);
    const onQueue = () => refreshPending();
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('vd-queue-changed', onQueue);
    refreshPending();
    syncNow();
    const id = setInterval(() => { if (navigator.onLine) syncNow(); }, SYNC_INTERVAL_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('vd-queue-changed', onQueue);
      clearInterval(id);
    };
  }, [syncNow, refreshPending]);

  return (
    <OfflineContext.Provider value={{ online, pending, syncing, lastSync, syncNow }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() { return useContext(OfflineContext); }
